/**
 * PushNotificationsTab.tsx - Push notification management for Admin Dashboard
 * 
 * Features:
 * - Send push notifications to all users or specific segments
 * - View notification history
 * - Manage notification templates
 * 
 * @author Heseda AI Team
 * @version 1.0
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './PushNotificationsTab.css';

interface PushNotificationsTabProps {
  language: string;
}

type NotificationTarget = 'all' | 'topic' | 'user';

interface NotificationForm {
  target: NotificationTarget;
  topic: string;
  userId: string;
  title: string;
  body: string;
  imageUrl: string;
  actionType: string;
}

const PREDEFINED_TOPICS = [
  { value: 'new_products', labelEn: 'New Products', labelKo: '신제품 알림' },
  { value: 'promotions', labelEn: 'Promotions & Sales', labelKo: '프로모션 및 할인' },
  { value: 'skin_tips', labelEn: 'Skin Care Tips', labelKo: '피부 관리 팁' },
];

const ACTION_TYPES = [
  { value: '', labelEn: 'None (just open app)', labelKo: '없음 (앱 열기)' },
  { value: 'open_chat', labelEn: 'Open Chat', labelKo: '채팅 열기' },
  { value: 'open_profile', labelEn: 'Open Profile', labelKo: '프로필 열기' },
];

const PushNotificationsTab: React.FC<PushNotificationsTabProps> = ({ language }) => {
  const { t } = useTranslation('admin');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  
  const [form, setForm] = useState<NotificationForm>({
    target: 'all',
    topic: '',
    userId: '',
    title: '',
    body: '',
    imageUrl: '',
    actionType: '',
  });

  const handleInputChange = useCallback((field: keyof NotificationForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setResult(null); // Clear previous result
  }, []);

  const handleSendNotification = useCallback(async () => {
    // Validation
    if (!form.title.trim()) {
      setResult({ success: false, message: t('notifications.validation.titleRequired') });
      return;
    }
    if (!form.body.trim()) {
      setResult({ success: false, message: t('notifications.validation.bodyRequired') });
      return;
    }
    if (form.target === 'topic' && !form.topic) {
      setResult({ success: false, message: t('notifications.validation.topicRequired') });
      return;
    }
    if (form.target === 'user' && !form.userId.trim()) {
      setResult({ success: false, message: t('notifications.validation.userIdRequired') });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const apiBase = getApiBaseUrl();
      
      // Build request payload
      const payload: any = {
        notification: {
          title: form.title,
          body: form.body,
          ...(form.imageUrl && { imageUrl: form.imageUrl }),
          ...(form.actionType && { data: { action: form.actionType } }),
        },
        saveToHistory: true,
      };

      // Set target
      if (form.target === 'all') {
        payload.topic = 'all_users';
      } else if (form.target === 'topic') {
        payload.topic = form.topic;
      } else if (form.target === 'user') {
        payload.userId = form.userId;
      }

      const response = await authFetch(`${apiBase}/sendPushNotification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ 
          success: true, 
          message: t('notifications.sendSuccess', { count: data.successCount || 0 })
        });
        // Reset form
        setForm(prev => ({ ...prev, title: '', body: '', imageUrl: '' }));
      } else {
        setResult({ 
          success: false, 
          message: data.error || t('notifications.sendFailed')
        });
      }
    } catch (error) {
      console.error('Send notification error:', error);
      setResult({ 
        success: false, 
        message: t('notifications.networkError')
      });
    } finally {
      setSending(false);
    }
  }, [form, language]);

  return (
    <div className="push-notifications-tab">
      <div className="notification-form-section">
        <h3>{t('notifications.title')}</h3>
        
        {/* Target Selection */}
        <div className="form-group">
          <label>{t('notifications.targetLabel')}</label>
          <div className="target-buttons">
            <button 
              className={`target-btn ${form.target === 'all' ? 'active' : ''}`}
              onClick={() => handleInputChange('target', 'all')}
            >
              {t('notifications.targetAll')}
            </button>
            <button 
              className={`target-btn ${form.target === 'topic' ? 'active' : ''}`}
              onClick={() => handleInputChange('target', 'topic')}
            >
              {t('notifications.targetTopic')}
            </button>
            <button 
              className={`target-btn ${form.target === 'user' ? 'active' : ''}`}
              onClick={() => handleInputChange('target', 'user')}
            >
              {t('notifications.targetUser')}
            </button>
          </div>
        </div>

        {/* Topic Selection (conditional) */}
        {form.target === 'topic' && (
          <div className="form-group">
            <label>{t('notifications.topicField')}</label>
            <select 
              value={form.topic} 
              onChange={(e) => handleInputChange('topic', e.target.value)}
            >
              <option value="">{t('notifications.selectTopic')}</option>
              {PREDEFINED_TOPICS.map(topic => (
                <option key={topic.value} value={topic.value}>
                  {t(`notifications.topics.${topic.value}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* User ID (conditional) */}
        {form.target === 'user' && (
          <div className="form-group">
            <label>{t('notifications.userIdField')}</label>
            <input
              type="text"
              value={form.userId}
              onChange={(e) => handleInputChange('userId', e.target.value)}
              placeholder="e.g., abc123xyz456..."
            />
          </div>
        )}

        {/* Notification Content */}
        <div className="form-group">
          <label>{t('notifications.titleField')}</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder={t('notifications.titlePlaceholder')}
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label>{t('notifications.bodyField')}</label>
          <textarea
            value={form.body}
            onChange={(e) => handleInputChange('body', e.target.value)}
            placeholder={t('notifications.bodyPlaceholder')}
            rows={3}
            maxLength={500}
          />
          <span className="char-count">{form.body.length}/500</span>
        </div>

        <div className="form-group">
          <label>{t('notifications.imageUrlField')}</label>
          <input
            type="url"
            value={form.imageUrl}
            onChange={(e) => handleInputChange('imageUrl', e.target.value)}
            placeholder="https://example.com/image.png"
          />
        </div>

        <div className="form-group">
          <label>{t('notifications.tapActionField')}</label>
          <select 
            value={form.actionType} 
            onChange={(e) => handleInputChange('actionType', e.target.value)}
          >
            {ACTION_TYPES.map(action => (
              <option key={action.value} value={action.value}>
                {t(`notifications.actions.${action.value || 'none'}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Result Message */}
        {result && (
          <div className={`result-message ${result.success ? 'success' : 'error'}`}>
            {result.success ? '✅' : '❌'} {result.message}
          </div>
        )}

        {/* Send Button */}
        <button 
          className="send-notification-btn"
          onClick={handleSendNotification}
          disabled={sending}
        >
          {sending 
            ? t('notifications.sending')
            : t('notifications.send')
          }
        </button>
      </div>

      {/* Info Section */}
      <div className="info-section">
        <h4>{t('notifications.guide.title')}</h4>
        <ul>
          <li>
            <strong>{t('notifications.guide.allUsersLabel')}</strong>{' '}
            {t('notifications.guide.allUsersDesc')}
          </li>
          <li>
            <strong>{t('notifications.guide.topicLabel')}</strong>{' '}
            {t('notifications.guide.topicDesc')}
          </li>
          <li>
            <strong>{t('notifications.guide.specificUserLabel')}</strong>{' '}
            {t('notifications.guide.specificUserDesc')}
          </li>
        </ul>
        <p className="note">
          {t('notifications.guide.firebaseNote')}
        </p>
      </div>
    </div>
  );
};

export default PushNotificationsTab;
